import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import {
  finalizeInteraction,
  IdpProxyError,
  submitOauthAuthorize,
} from '@ayeldo/frontend-core/auth/idpProxy';
import { isSafeRedirectUrl } from '@ayeldo/frontend-core/auth/redirectGuard';
import { resolveAuthAllowedRedirectHosts } from '@ayeldo/frontend-core/auth/resolveAuthAllowedRedirectHosts';

// Resolve the allow-list once at module load time so it is stable across renders.
const allowedHosts = resolveAuthAllowedRedirectHosts(import.meta.env);

const DEFAULT_LOCAL_IDP_URL = 'http://localhost:3010';

const pageContainerSx = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const errorContentSx = {
  maxWidth: 400,
  textAlign: 'center',
} as const;

function resolveIdpApiBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_URL;
  const trimmed = typeof configured === 'string' ? configured.trim() : '';
  if (trimmed !== '') return trimmed;
  if (import.meta.env.DEV) return DEFAULT_LOCAL_IDP_URL;
  throw new Error('VITE_API_URL is not configured for production build');
}

/**
 * Extracts the `interaction` query parameter value from a sign-in URL returned
 * by POST /api/v1/oauth/authorize. Returns null when the URL does not contain one.
 */
function extractInteractionId(signInUrl: string): string | null {
  try {
    const url = new URL(signInUrl);
    const interaction = url.searchParams.get('interaction');
    return interaction !== null && interaction.trim().length > 0 ? interaction : null;
  } catch {
    return null;
  }
}

function hasInteractionId(interactionId: string | null): interactionId is string {
  return interactionId !== null;
}

function hasFinalizeResult(
  result: { redirectUri: string } | null
): result is { redirectUri: string } {
  return result !== null;
}

function isFinalizeError(err: unknown): err is unknown {
  return err !== null;
}

function isUnauthenticatedIdpError(err: unknown): boolean {
  return err instanceof IdpProxyError && err.status === 401;
}

async function attemptSilentFinalize(
  interactionId: string,
  idpApiBase: string
): Promise<{ result: { redirectUri: string } | null; err: unknown }> {
  try {
    const result = await finalizeInteraction(interactionId, { idpApiBase });
    return { result, err: null };
  } catch (err) {
    return { result: null, err };
  }
}

/**
 * OauthAuthorizePage — React proxy route for /oauth/authorize.
 *
 * The studio redirects the browser here with OIDC params in the query string.
 * This page POSTs to the IDP API and attempts to silently finalize the interaction
 * if an IDP session already exists (i.e. the user is already authenticated at the IdP).
 * If no session exists, it falls back to navigating to the sign-in page.
 */
export function OauthAuthorizePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const doAuthorize = async () => {
      try {
        const idpApiBase = resolveIdpApiBaseUrl();
        const result = await submitOauthAuthorize(buildAuthorizeParams(searchParams), {
          idpApiBase,
        });

        if (cancelled) return;

        if (!isSafeRedirectUrl(result.signInUrl, allowedHosts)) {
          // eslint-disable-next-line no-console
          console.warn('OauthAuthorizePage: rejected redirect target', result.signInUrl);
          setStatus('error');
          setErrorMessage('Redirect blocked for safety. Please try again or contact support.');
          return;
        }

        const interactionId = extractInteractionId(result.signInUrl);

        if (hasInteractionId(interactionId)) {
          await handleSilentFinalize(
            interactionId,
            idpApiBase,
            result.signInUrl,
            cancelled,
            setStatus,
            setErrorMessage
          );
          return;
        }

        // No interaction id in signInUrl — navigate directly (existing behavior).
        window.location.href = result.signInUrl;
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(err instanceof Error ? err.message : 'Sign-in could not be started.');
        }
      }
    };

    void doAuthorize();

    return () => {
      cancelled = true;
    };
    // EXCEPTION: searchParams is a stable hook value but eslint cannot statically prove it
    // here. The OAuth authorize call must run exactly once on mount with the URL params
    // captured at that moment; including searchParams would risk re-running on hash changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'loading') {
    return (
      <Box sx={pageContainerSx}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Please wait...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      <Stack spacing={2} alignItems="center" sx={errorContentSx}>
        <Typography variant="h5" component="h1">
          Sign-in Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {errorMessage ?? 'Sign-in could not be started. Please try again.'}
        </Typography>
      </Stack>
    </Box>
  );
}

function buildAuthorizeParams(searchParams: URLSearchParams) {
  const state = searchParams.get('state');
  const nonce = searchParams.get('nonce');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  return {
    client_id: searchParams.get('client_id') ?? '',
    redirect_uri: searchParams.get('redirect_uri') ?? '',
    response_type: searchParams.get('response_type') ?? '',
    scope: searchParams.get('scope') ?? '',
    ...(state !== null ? { state } : {}),
    ...(nonce !== null ? { nonce } : {}),
    ...(codeChallenge !== null ? { code_challenge: codeChallenge } : {}),
    ...(codeChallengeMethod !== null ? { code_challenge_method: codeChallengeMethod } : {}),
  };
}

async function handleSilentFinalize(
  interactionId: string,
  idpApiBase: string,
  signInUrl: string,
  cancelled: boolean,
  setStatus: (s: 'loading' | 'error') => void,
  setErrorMessage: (m: string) => void
): Promise<void> {
  // Attempt silent re-authentication: the user may already have an idp_session.
  // POST /api/v1/interaction/{uid}/finalize returns the OIDC redirect URI on
  // success, or 401 when no session is present.
  const { result: finalizeResult, err: finalizeErr } = await attemptSilentFinalize(
    interactionId,
    idpApiBase
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (cancelled) return;

  if (isFinalizeError(finalizeErr)) {
    if (isUnauthenticatedIdpError(finalizeErr)) {
      // Not authenticated at the IdP — fall through to the sign-in page.
      window.location.href = signInUrl;
      return;
    }

    // Any other error (network failure, 5xx, etc.) — show the error UI.
    setStatus('error');
    setErrorMessage(
      finalizeErr instanceof Error ? finalizeErr.message : 'Sign-in could not be started.'
    );
    return;
  }

  if (hasFinalizeResult(finalizeResult)) {
    if (!isSafeRedirectUrl(finalizeResult.redirectUri, allowedHosts)) {
      // eslint-disable-next-line no-console
      console.warn(
        'OauthAuthorizePage: rejected finalize redirect target',
        finalizeResult.redirectUri
      );
      setStatus('error');
      setErrorMessage('Redirect blocked for safety. Please try again or contact support.');
      return;
    }

    // Silent auth succeeded — navigate directly to the OIDC redirect URI.
    window.location.href = finalizeResult.redirectUri;
    return;
  }

  // finalizeResult is null and no error — navigate to sign-in as fallback.
  window.location.href = signInUrl;
}
